import React, {
  RefObject,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  MultiMediaResponse,
  PatientMediaSearchResultDTO,
} from 'dtos';
import {
  SortOrder,
} from 'dtos/sortOrder';
import UploadIcon from 'core/icons/upload';
import Table from 'core/components/VirtualTable';
import ToolTip from 'core/components/Tooltip';
import {
  useTableReset,
} from 'core/hooks';
import useStorageState from 'core/hooks/storage';
import {
  Props,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import {
  faxEventChannel,
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import ConsentFormButton from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/ConsentFormButton';
import Button from 'pages/Dashboard/components/Button';
import {
  getPatientsMultimedia,
} from 'pages/Dashboard/services/api';
import {
  cardViewColumns,
  listViewColumns,
  MultimediaContextType,
  MultimediaCard,
  CardSkeleton,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/columns';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  FaxIcon,
} from 'core/icons';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import GridViewIcon from '@mui/icons-material/GridView';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ButtonGroup from '@mui/material/ButtonGroup';
import isNil from 'lodash/isNil';
import EditDialog from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/EditDialog';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import MultimediaSignatureDialog from 'views/EMR/Charts/MultimediaSignatureDialog';
import dayjs from 'utils/dayjs';
import {
  generateDropzoneAccept,
} from 'core/mimeMap';
import TabFilter from 'views/EMR/components/TabFilter';
import {
  useGetMediaCategories,
  useGetMediaCategoryTabOptions,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/hooks';
import {
  isEmptyString,
} from '@populateui/utils';
import {
  TableProvider,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/TableContext';
import PatientMultimediaLookup from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/PatientMultimediaLookup';
import Divider from '@mui/material/Divider';
import {
  usePatientGetMultimediaByMedia,
} from 'views/EMR/hooks/media';
import head from 'lodash/head';
import SortBy, {
  SortOption,
  SortValue,
} from 'views/EMR/components/SortBy';
import clsx from 'clsx';
import useBrowserBackHandler from 'core/hooks/useBrowserBackHandler';

type MultimediaSortKey = 'FileName' | 'Timestamp';
type MultimediaSortValue = SortValue<MultimediaSortKey>;

const defaultMultimediaSortValue: MultimediaSortValue = {
  sortBy: 'Timestamp',
  sortOrder: SortOrder.Descending,
};

const multimediaSortOptions: SortOption<MultimediaSortKey>[] = [
  {
    label: 'File name',
    value: 'FileName',
    defaultSortOrder: SortOrder.Ascending,
    directionOptions: [
      { label: 'A to Z', value: SortOrder.Ascending },
      { label: 'Z to A', value: SortOrder.Descending },
    ],
  },
  {
    label: 'Date uploaded',
    value: 'Timestamp',
    defaultSortOrder: SortOrder.Ascending,
    directionOptions: [
      { label: 'Old to new', value: SortOrder.Ascending },
      { label: 'New to old', value: SortOrder.Descending },
    ],
  },
];

export default function Multimedia({
  patient,
}: Props) {
  const userInfo = useUserInfo();
  const { tableInstanceRef, reset } = useTableReset();

  const { refetch } = tableInstanceRef?.current ?? {};

  const isSearchSelectionRef = useRef(false);
  const [clearSearchTrigger, setClearSearchTrigger] = useState(0);
  const [
    selectedSearchMedia,
    setSelectedSearchMedia,
  ] = useState<PatientMediaSearchResultDTO | null>(null);
  const [viewMode, setViewMode] = useStorageState<'card' | 'list'>(
    'multimedia_viewMode',
    'card',
    'local',
  );

  const [sortValue, setSortValue] = useStorageState<MultimediaSortValue>(
    'multimedia_sortValue',
    defaultMultimediaSortValue,
    'local',
  );

  const {
    data: searchResultData,
    isLoading: isSearchResultLoading,
  } = usePatientGetMultimediaByMedia({
    mediaId: selectedSearchMedia?.patientMultimediaId,
  });

  const searchResultMedia = useMemo(
    () => head(searchResultData?.list),
    [searchResultData],
  );

  const {
    state: editMediaParams,
    open: setEditMediaParams,
  } = useBrowserBackHandler<MultiMediaResponse>({
    stateName: 'mediaDialog',
  });

  const [
    signMediaParams,
    setSignMediaParams,
  ] = useState<MultiMediaResponse | null>(null);

  const refetchTableData = async () => {
    await tableInstanceRef?.current?.refetch?.();
  };

  useEffect(() => {
    const unsubscribeFromDeleteMedia = mediaEventChannel.on('onMediaDeleted', async (mediaPayload) => {
      const selectedMediaWasDeleted = mediaPayload.list?.some(
        ({ mediaId }) => mediaId === selectedSearchMedia?.patientMultimediaId,
      );
      if (selectedMediaWasDeleted) {
        setSelectedSearchMedia(null);
        setClearSearchTrigger((previousValue) => previousValue + 1);
      }

      await refetchTableData();
    });
    const unsubscribeFromUpdate = mediaEventChannel.on('onMediaUpdated', refetchTableData);
    const unsubscribeFromSignatureDialog = mediaEventChannel.on('onMediaSignatureDialogOpen', setSignMediaParams);
    return () => {
      unsubscribeFromDeleteMedia();
      unsubscribeFromUpdate();
      unsubscribeFromSignatureDialog();
    };
  }, [selectedSearchMedia?.patientMultimediaId]);

  const allowedFileTypes = useGetAllowedFileTypes();
  const cleanExtensions = allowedFileTypes.split(',')
    .map((ext) => ext.trim().replace(/^\./, ''));

  const dropzoneAccept = generateDropzoneAccept(cleanExtensions);

  const onEditMedia = async (data: MultiMediaResponse) => {
    const expiresOn = data.url?.expiresOn;
    const isExpired = !isNil(expiresOn) && dayjs(expiresOn).isBefore(dayjs());
    if (isExpired) {
      const response = await refetch?.();
      const row = ((response?.data ?? []) as MultiMediaResponse[])
        .find((row) => row.mediaId === data.mediaId);
      if (!isNil(row)) {
        setEditMediaParams(row);
      }
    } else {
      setEditMediaParams(data);
    }
  };

  const tabOptions = useGetMediaCategoryTabOptions();
  const [selectedCategory, selectCategory] = useState<string>();
  const categories = useGetMediaCategories();

  const isCardView = viewMode === 'card';
  const isListView = viewMode === 'list';

  useEffect(() => {
    reset();
  }, [selectedCategory, sortValue, viewMode]);

  return (
    <div className="-mx-4 -mb-4 flex flex-col !h-[calc(100%+16px)]">
      <div className="flex gap-4 flex-col w-full h-full">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 w-full px-4">
            <div className="flex gap-4 flex-1 justify-between w-full">
              <div className="flex items-center gap-4">
                <PatientMultimediaLookup
                  patientId={patient?.patientId ?? 0}
                  onSelected={(selected) => {
                    if (!isNil(selected)) {
                      isSearchSelectionRef.current = true;
                      selectCategory(selected?.mediaCategory ?? selectedCategory);
                      setSelectedSearchMedia(selected);
                    }
                  }}
                  onClear={() => {
                    setSelectedSearchMedia(null);
                  }}
                  clearTrigger={clearSearchTrigger}
                />
                <Divider orientation="vertical" flexItem />
                <div className="flex items-center gap-4">
                  <ButtonGroup variant="outlined" color="inherit">
                    <ToolTip title="Card View">
                      <Button
                        className={clsx(
                          'flex gap-2 !h-8 !min-w-0',
                          { '!bg-blue-50 !text-blue-600': isCardView },
                          { '!text-gray-600': !isCardView },
                        )}
                        type="button"
                        variant="outlined"
                        onClick={() => { setViewMode('card'); }}
                      >
                        <GridViewIcon className="!w-4 !h-4 !text-inherit" />
                      </Button>
                    </ToolTip>
                    <ToolTip title="List View">
                      <Button
                        className={clsx(
                          'flex gap-2 !h-8 !min-w-0',
                          { '!bg-blue-50 !text-blue-600': isListView },
                          { '!text-gray-600': !isListView },
                        )}
                        type="button"
                        variant="outlined"
                        onClick={() => { setViewMode('list'); }}
                      >
                        <FormatListBulletedIcon className="!w-4 !h-4 !text-inherit" />
                      </Button>
                    </ToolTip>
                  </ButtonGroup>
                  <SortBy
                    value={sortValue}
                    options={multimediaSortOptions}
                    onChange={setSortValue}
                  />
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <ConsentFormButton patient={patient} />
                {!isNil(userInfo?.organization?.eFaxConfig) && (
                  <ToolTip title="Send Fax">
                    <Button
                      className="!h-8"
                      type="button"
                      color="inherit"
                      variant="outlined"
                      onClick={() => { faxEventChannel.emit('onOpenSendFax', { patient }); }}
                    >
                      <FaxIcon className="!w-4 !h-4 !text-inherit" />
                    </Button>
                  </ToolTip>
                )}
                <Divider orientation="vertical" flexItem />
                <ToolTip title="Upload Media">
                  <Button
                    className="!h-8"
                    color="primary"
                    variant="outlined"
                    onClick={() => {
                      mediaEventChannel.emit('onUploadMediaToCharts', {
                        dropzoneAccept,
                        patientId: patient?.patientId ?? 0,
                        defaultCategory: selectedCategory,
                        onUpdate: async () => {
                          await refetch?.();
                        },
                      });
                    }}
                  >
                    <UploadIcon className="!w-4 !h-4 !text-inherit" />
                  </Button>
                </ToolTip>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-between">
              <TabFilter
                key={selectedCategory}
                options={tabOptions}
                defaultTab={selectedCategory}
                onUpdate={(option) => {
                  selectCategory(option);
                  if (!isSearchSelectionRef.current) {
                    setSelectedSearchMedia(null);
                    setClearSearchTrigger((prev) => prev + 1);
                  }
                  isSearchSelectionRef.current = false;
                }}
                size="medium"
                theme="gray"
              />
            </div>
          </div>
        </div>
        {!isNil(selectedSearchMedia) && (
          <div className="flex flex-col gap-4 px-1">
            <h3 className="text-sm font-semibold text-gray-700 px-4">Search Result</h3>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] auto-rows-fr gap-4 px-2">
              {isSearchResultLoading || isNil(searchResultMedia) ? (
                <div className="px-1 w-full h-full flex flex-col">
                  <CardSkeleton />
                </div>
              ) : (
                <div
                  className="px-1 w-full h-full flex flex-col cursor-pointer"
                  onClick={() => { onEditMedia(searchResultMedia); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onEditMedia(searchResultMedia);
                    }
                  }}
                >
                  <MultimediaCard
                    media={searchResultMedia}
                    categories={categories}
                    refetch={refetchTableData}
                    highlightedMediaId={selectedSearchMedia?.patientMultimediaId ?? null}
                  />
                </div>
              )}
            </div>
            {isCardView && <Divider />}
          </div>
        )}
        <div className="flex flex-1 w-full h-full overflow-y-hidden">
          <TableProvider<MultimediaContextType>
            value={{
              categories,
              refetch: refetchTableData,
            }}
          >
            <Table<MultiMediaResponse>
              instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
              getRowId={(row) => row.timeStamp}
              columns={viewMode === 'card' ? cardViewColumns : listViewColumns}
              getData={getPatientsMultimedia}
              queryParams={{
                patientId: patient?.patientId,
                filterDTO: {
                  mediaCategoryList: selectedCategory === 'All' || isEmptyString(selectedCategory)
                    ? []
                    : [selectedCategory],
                },
                sortRequest: sortValue,
              }}
              onRowClick={(table, row) => {
                onEditMedia(row.original);
              }}
              defaultPagination={{
                pageSize: 25,
                pageIndex: 0,
              }}
              className={clsx(
                { '!pb-0 multimedia-grid-table': isCardView },
                { '!pb-0': isListView },
              )}
              classes={isCardView ? {
                scrollContainer: '!block !px-4 !pb-4 !pt-0',
                body: '!px-2',
                grid: '!grid !grid-cols-[repeat(auto-fill,minmax(220px,1fr))] !auto-rows-fr',
                header: '!hidden',
                outerRow: '!w-full !h-full !left-0 !relative !mb-0 !block !border-none before:!hidden',
                row: '!px-1 !w-full !h-full !flex !flex-col !border-none',
                cell: '!py-2 !h-full !w-full !block',
              } : {
                header: '!px-4',
                outerRow: '!w-[calc(100%-16px)] left-4',
              }}
              scrollbarGutter={isCardView ? 'initial' : undefined}
              skeleton={{ rows: 3 }}
              loadingSlot="Loading multimedia..."
              emptySlot="No multimedia found"
            />
          </TableProvider>
        </div>
        {!isNil(editMediaParams) && (
          <EditDialog
            {...editMediaParams}
            onClose={() => { setEditMediaParams(null); }}
          />
        )}
        {!isNil(signMediaParams) && (
          <MultimediaSignatureDialog
            {...signMediaParams}
            onComplete={async () => { await refetch?.(); }}
            onClose={() => { setSignMediaParams(null); }}
          />
        )}
      </div>
    </div>
  );
}
